import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';

const REMINDER_MS = 3 * 60 * 1000; // 3 minutes
const REMINDER_BLUE = '#4E7090';

export const confirmLeaveUnsavedMap = () =>
  window.confirm('You have unsaved changes on your floor map. Leave without saving?');

/**
 * Timed save reminder — uses booth undo history (same signal as the pulsing save icon).
 */
function SaveReminder({ canUndo, historyPastLength, loading, activeBoothFile, onSave }) {
  const [visible, setVisible] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(null);
  const [editAnchor, setEditAnchor] = useState(null);
  const prevPastLengthRef = useRef(0);
  const prevMapKeyRef = useRef(null);

  const enabled = Boolean(activeBoothFile?.KEY_ID && !activeBoothFile?.overview);

  // Reset when the active floor map file changes.
  useEffect(() => {
    const mapKey = activeBoothFile?.KEY_ID || null;
    if (prevMapKeyRef.current !== mapKey) {
      setVisible(false);
      setDismissedAt(null);
      setEditAnchor(null);
      prevPastLengthRef.current = 0;
      prevMapKeyRef.current = mapKey;
    }
  }, [activeBoothFile?.KEY_ID]);

  // Track undo history: new edit starts timer; undo/save clears it.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!canUndo) {
      setVisible(false);
      setDismissedAt(null);
      setEditAnchor(null);
      prevPastLengthRef.current = historyPastLength;
      return;
    }
    if (historyPastLength > prevPastLengthRef.current && historyPastLength > 1) {
      setEditAnchor(Date.now());
      setDismissedAt(null);
      setVisible(false);
    }
    prevPastLengthRef.current = historyPastLength;
  }, [enabled, canUndo, historyPastLength]);

  // Show toast after 3 minutes; dismiss restarts the interval.
  useEffect(() => {
    if (!enabled || !canUndo || visible || !editAnchor) {
      return undefined;
    }
    const anchor = dismissedAt || editAnchor;
    const delay = Math.max(0, REMINDER_MS - (Date.now() - anchor));
    const timerId = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timerId);
  }, [enabled, canUndo, visible, editAnchor, dismissedAt]);

  // Browser tab close / refresh warning.
  useEffect(() => {
    if (!enabled || !canUndo) {
      return undefined;
    }
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [enabled, canUndo]);

  if (!enabled || !visible) {
    return null;
  }

  return (
    <Paper
      elevation={6}
      role="alert"
      aria-live="assertive"
      sx={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: 1200,
        maxWidth: 360,
        overflow: 'hidden',
        pointerEvents: 'auto',
        boxShadow: '0 4px 18px rgba(78, 112, 144, 0.3)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: REMINDER_BLUE,
        }}
      >
        <SaveIcon sx={{ fontSize: 18, color: '#fff' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
          Save Reminder
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, backgroundColor: '#fff' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, textAlign: 'left' }}>
          Unsaved Changes Detected
        </Typography>
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', lineHeight: 1.45, mb: 1, textAlign: 'left' }}>
          It has been 3 minutes since you last saved your updates. Please save your Floor Map progress to ensure that no data is lost.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Button
            size="small"
            disabled={loading}
            onClick={() => {
              setVisible(false);
              setDismissedAt(Date.now());
            }}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: REMINDER_BLUE }}
          >
            Dismiss
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={loading}
            onClick={() => onSave && onSave()}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: REMINDER_BLUE,
              '&:hover': { backgroundColor: '#3d5a73' },
            }}
          >
            Save Now
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

const mapStateToProps = (state) => ({
  canUndo: state.booth_history.past.length > 1,
  historyPastLength: state.booth_history.past.length,
  loading: state.main.loading,
  activeBoothFile: state.main.active_booth_file,
});

export default connect(mapStateToProps)(SaveReminder);
